import { useLingui } from '@lingui/react/macro';
import { ArrowRight2 } from 'iconsax-reactjs';
import { type FC, memo } from 'react';
import { Link } from '@/base/router';
import { useSettings } from '@/settings/api';
import { DisplayQuoteToggleGroup } from '@/settings/DisplayQuoteToggleGroup';
import { useCoin } from '@/shared/api';
import { Digits } from '@/shared/Digits';

type CoinPriceSectionProps = {
  slug?: string;
  price?: string;
};

// Only these pairs have a dedicated (prerendered) converter page — see
// CONVERTER_PAIRS in prerender.routes.mjs.
const CONVERTER_COINS = ['BTC', 'ETH', 'USDT', 'SOL'];

export const CoinPriceSection: FC<CoinPriceSectionProps> = memo(({ slug }) => {
  const { t } = useLingui();
  const [settings] = useSettings();
  const { data: coin } = useCoin(slug?.toUpperCase());

  const displayQuote = settings.displayQuote;
  const priceValue =
    displayQuote === 'iqd'
      ? coin?.market_data?.price_iqd
      : coin?.market_data?.price;
  const priceChangePercent = coin?.market_data?.change_24h;
  const showConverterLink = !!coin && CONVERTER_COINS.includes(coin.slug);

  return (
    <div className="flex desktop:flex-row desktop:gap-0 gap-3 flex-col desktop:items-center justify-between w-full desktop:h-12 h-16">
      <div className="flex items-center gap-2">
        <Digits
          value={priceValue}
          coin={displayQuote}
          className="text-[24px] desktop:text-[40px] leading-none font-medium text-content-primary"
        />
        <div className="flex items-center gap-2 text-sm">
          <Digits
            value={priceChangePercent ?? ''}
            unit="%"
            color="auto"
            sign="always"
            className="text-sm desktop:text-lg"
          />
          <span className="text-content-tertiary text-sm desktop:text-base">
            (24H)
          </span>
        </div>
      </div>
      <div className="desktop:w-auto w-full flex flex-col gap-2 desktop:items-end">
        <DisplayQuoteToggleGroup size={8} className="w-full" variant="button" />
        {showConverterLink && (
          <Link
            to={`/${settings.language}/converter/${coin.slug}/IQD`}
            className="text-xs text-content-link hover:underline flex items-center gap-1"
          >
            {t`Convert ${coin.symbol} to IQD`}
            <ArrowRight2 className="auto-rtl" size={10} />
          </Link>
        )}
      </div>
    </div>
  );
});
